<script setup lang="ts">
// メニュー開閉状態
import { ref, computed } from "vue";
const isOpen = ref(false);

// メニューを非表示にするページ判定
const route = useRoute();
const isMenuHidden = computed(() => {
  return route.path.includes("/fire/compare") ||
    route.path === "/info/terms" ||
    route.path === "/info/privacy-policy";
});

// メニュー開閉トグル
const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};

// メニューを閉じる関数
const closeMenu = () => {
  isOpen.value = false;
};
</script>

<template>
  <header class="header" :class="{
    'header_no-sticky': isMenuHidden
  }">
    <div class="header_inner">
      <div class="header_logo">
        <!-- 試算表ページではリンクなし、通常ページではリンクあり -->
        <template v-if="isMenuHidden" class="header_logo-link">
          <img src="../assets/images/logo.png" alt="ロゴ" class="header_logo-img" />
        </template>
        <template v-else>
          <NuxtLink to="/" class="header_logo-link">
            <img src="../assets/images/logo.png" alt="ロゴ" class="header_logo-img" />
          </NuxtLink>
        </template>
      </div>
      <!-- SP用ハンバーガーメニュー/試算表、利用規約、プラポリの場合存在しない-->
      <button v-if="!isMenuHidden" class="header_menu-btn" type="button" @click="toggleMenu" aria-label="メニューを開閉"
        :aria-expanded="isOpen">
        <!-- アイコンコンテナ - 両方のアイコンを同じ位置に配置 -->
        <div class="header_menu-icon-container">
          <!-- bars-3アイコン - メニュー閉じている時に表示 -->
          <Icons name="bars-3" class="header_menu-icon" :class="{ 'icon-hidden': isOpen }" />
          <!-- x-markアイコン - メニュー開いている時に表示 -->
          <Icons name="x-mark" class="header_menu-icon" :class="{ 'icon-hidden': !isOpen }" />
        </div>
        <span class="header_menu-label">MENU</span>
      </button>
      <!-- PC用ナビゲーション 試算表、利用規約、プラポリの場合存在しない -->
      <nav v-if="!isMenuHidden" class="header_nav">
        <NavList />
      </nav>
    </div>
  </header>

  <!-- SP用メニューオーバーレイ - headerの外に移動 -->
  <transition name="slide-only">
    <div v-if="isOpen && !isMenuHidden" class="header-overlay">
    </div>
  </transition>

  <!-- NavList -->
  <transition name="fade-only">
    <div v-if="isOpen && !isMenuHidden" class="nav-floating">
      <NavList @link-click="closeMenu" />
    </div>
  </transition>
</template>

<style scoped lang="scss">
// モバイル用のスタイル
.header {
  position: sticky;
  top: 0;
  z-index: 10;
  backdrop-filter: blur(0.75rem);
  background-color: rgba(255, 255, 255, 0.64);

  // 試算表ページでは吸顶効果を無効化
  &_no-sticky {
    position: static;
  }

  &_inner {
    display: flex;
    align-items: center;
    padding: 0.5rem 0;
    width: min(91vw, 64.5rem);
    margin: auto;
    height: 2.5rem;
  }

  &_logo {
    display: flex;
    align-items: center;
    height: 2.5rem;
    width: auto;

    &-link {
      display: flex;
      align-items: center;
      height: 100%;
    }

    &-img {
      height: 100%;
      width: auto;
      object-fit: contain;
    }
  }

  &_menu-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    margin-left: auto;
    outline: none;
  }

  &_menu-label {
    font-size: 0.5rem;
    color: #2174C0;
    font-weight: 700;
    text-align: center;
    line-height: 1.5;
  }

  &_nav {
    display: none;
  }
}

// アイコンコンテナ - 両方のアイコンを重ねて配置
.header_menu-icon-container {
  position: relative;
  width: 1.5rem;
  height: 1.5rem;
}

// アイコンのベーススタイル
.header_menu-icon {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 1;
  transition: opacity 0.3s ease;

  &.icon-hidden {
    opacity: 0;
  }
}

// header-overlay用スライドアニメーション
.slide-only-enter-active {
  transition: transform 0.3s ease-out;
}

.slide-only-leave-active {
  transition: transform 0.25s ease-in;
}

.slide-only-enter-from {
  transform: translateY(-100%);
}

.slide-only-leave-to {
  transform: translateY(-100%);
}

.slide-only-enter-to,
.slide-only-leave-from {
  transform: translateY(0);
}

// NavList用フェードアニメーション
.fade-only-enter-active {
  transition: opacity 0.3s ease;
}

.fade-only-leave-active {
  transition: opacity 0.2s ease;
}

.fade-only-enter-from,
.fade-only-leave-to {
  opacity: 0;
}

.fade-only-enter-to,
.fade-only-leave-from {
  opacity: 1;
}

// SP用メニューオーバーレイ（背景のみ）
.header-overlay {
  position: fixed;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 255, 255, 1);
  z-index: 9;
}

.nav-floating {
  position: fixed;
  top: 3.5rem;
  left: 1rem;
  width: calc(100vw - 2rem);
  height: calc(100vh - 3.5rem);
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  :deep(nav) {
    pointer-events: auto;
  }
}

// PC用のスタイル
@include mixin.media(pc) {
  .header {
    &_inner {
      padding: 0.25rem 0;
      height: 3rem;
    }

    &_logo {
      height: 3rem;
    }

    &_menu-btn {
      display: none;
    }

    &_nav {
      display: flex;
      flex: 1 1 auto;
      justify-content: flex-end;
      align-items: center;
    }
  }

  .header-overlay,
  .nav-floating {
    display: none;
  }
}
</style>
